import { Box } from '@mui/material'
import { useAuth } from '../../../context/AuthContext'
import { useLocation, useOutletContext } from 'react-router-dom'
import { useEffect, useState, useMemo } from 'react'
import { editObject, getObject, addObject } from '../../../api/apiClientCrud'
import { toast } from 'react-toastify'
import useNavigate from '../../hooks/useNavigate'
import dayjs from 'dayjs'
import Form from '../../form/Form'
import Toolbar from '../../toolbar/Toolbar'
import { getIncomeFields } from './AccountingFields'
import useSaveEntity from '../../hooks/useSaveEntity'
import { useFetchEntities } from '../../hooks/useFetchEntities'

const ROUTES = {
  EDIT: '/incomes/edit',
  VIEW: '/incomes/view',
  ADD: '/incomes/add'
}

const buildPayload = ({ data, user, mode }) => {
  const common = {
    periodYear: dayjs(data.date).format('YYYY'),
    periodMonth: dayjs(data.date).format('MM'),
    periodYearMonth: dayjs(data.date).format('YYYYMM')
  }

  return mode === 'edit'
    ? { ...data, ...common, lastModBy: user.email }
    : { ...data, ...common, company_id: user.company_id, createdBy: user.email }
}

function Income({ modeProps = null, checkForm, setCheckForm, value, setReturnValue, onValidSubmit }) {
  const location = useLocation()
  const { user } = useAuth()
  const userRight = useOutletContext()
  const [editIncome, setEditIncome] = useState({})
  const [isSubmit, setIsSubmit] = useState(false)
  const history = useNavigate()
  const [displayMode, setDisplayMode] = useState(modeProps)
  const path = location.pathname
  const incomeId = location.state?.id

  const mode = useMemo(() => {
    if (displayMode === 'dialog') return 'add'
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path, displayMode])

  const incomeQuery =
    mode === 'edit' || mode === 'view' ? { key: 'income', fn: getObject, args: ['incomes', incomeId] } : null
  const { data: fetched, loading: loadingEntities } = useFetchEntities([incomeQuery].filter(Boolean))

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'incomes',
    mode,
    id: incomeId,
    user,
    args: [editIncome],
    addFn: addObject,
    editFn: editObject,
    buildPayload,
    onSuccess: async (res) => {
      const { id, invoice_id: invoiceId, lastModDateTime } = res.data

      if (!lastModDateTime) {
        try {
          const resE = await editObject('invoices', invoiceId, {
            status: 80,
            lastModBy: user.email
          })

          if (displayMode === 'dialog') {
            setReturnValue?.(resE.data)
            onValidSubmit?.()
          }
        } catch {
          toast.error('Erreur lors de la mise à jour de la facture', { autoClose: 2000 })
        }
      }
    },

    onError: (err) => {
      toast.error('Erreur lors de la sauvegarde', { autoClose: 2000 })
    },
    onSuccessRedirect: (id) => {
      if (displayMode !== 'dialog') {
        history(location.state?.history || ROUTES.VIEW, { state: { id: id } })
      }
    }
  })

  const toolbar = useMemo(
    () => ({
      accessRight: userRight,
      title: () => {
        if (path.includes('/add')) return 'Nouvel Encaissement'

        const invoiceNumber = editIncome?.invoice?.invoice_number
        return invoiceNumber ? `Encaissement Facture ${invoiceNumber}` : 'Encaissement'
      },
      objectType: 'incomes',
      actions: { edit: true, back: true, audits: true, list: true }
    }),
    [userRight, editIncome, path]
  )

  const fields = useMemo(() => getIncomeFields(user, displayMode, location), [user, displayMode, location])

  const formOptions = useMemo(
    () => ({
      fields,
      data: editIncome,
      location
    }),
    [fields, editIncome, location]
  )

  useEffect(() => {
    let isMounted = true

    const fetchInvoice = async () => {
      if (!editIncome?.invoice_id) return
      try {
        const res = await getObject('invoices', editIncome.invoice_id)
        const invoice = res.data
        if (!isMounted || !invoice) return

        setEditIncome((prev) => ({
          ...prev,
          customer_id: invoice.customer_id
        }))
      } catch (err) {}
    }

    fetchInvoice()
    return () => {
      isMounted = false
    }
  }, [editIncome?.invoice_id])

  useEffect(() => {
    if (displayMode === 'dialog' && value) {
      setEditIncome((prev) => ({
        ...prev,
        invoice_id: value.id,
        customer_id: value.customer_id,
        amount: value.net_amount
      }))
    }
  }, [displayMode, value])

  useEffect(() => {
    if (!loadingEntities && fetched?.income) {
      setEditIncome(fetched.income)
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    if (isSubmit) {
      saveEntity({ data: editIncome })
      setIsSubmit(false)
    }
  }, [isSubmit, editIncome, saveEntity])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flexGrow: 1
      }}
    >
      {displayMode !== 'dialog' && <Toolbar options={toolbar} object={editIncome} />}
      <Form
        mode={mode}
        options={formOptions}
        modeProps={modeProps}
        setDataParent={setEditIncome}
        setIsSubmitParent={setIsSubmit}
        checkForm={checkForm}
        setCheckForm={setCheckForm}
        loading={loading}
      />
    </Box>
  )
}

export default Income
